import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ExternalLink, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";

const projectCategories = [
  { name: "All", slug: "all" },
  { name: "Web Design", slug: "web-design" },
  { name: "E-commerce", slug: "ecommerce" },
  { name: "Branding", slug: "branding" },
  { name: "Live Chat", slug: "live-chat" },
];

const projects = [
  {
    id: 1,
    title: "Block P Trainers",
    client: "Block P",
    category: "ecommerce",
    categoryLabel: "E-commerce",
    description: "Complete Shopify redesign for a leading Merseyside trainer retailer. We worked closely with Block P to increase conversions and give the site a more professional, premium look.",
    services: ["Shopify Redesign", "E-commerce", "Conversion Optimisation", "UI/UX Design"],
    results: [
      "Increased conversion rates",
      "Professional premium design",
      "Improved user experience"
    ],
    image: "/screenshots/blockp.png",
    mobileImage: "/screenshots/blockp-mobile.png",
    url: "https://www.blockp.co.uk",
    color: "pink"
  },
  {
    id: 2,
    title: "iSalon Hair & Beauty",
    client: "iSalon UK",
    category: "web-design",
    categoryLabel: "Web Design",
    description: "Complete website for a local hair salon featuring a full booking request system and integrated AI live chat agent to handle enquiries 24/7.",
    services: ["Website Design", "Booking System", "Live Chat Integration"],
    results: [
      "24/7 automated booking requests",
      "Live chat agent handles enquiries",
      "Increased online bookings"
    ],
    image: "/screenshots/isalon.png",
    mobileImage: "/screenshots/isalon-mobile.png",
    url: "https://www.isalon.uk",
    color: "blue"
  },
  {
    id: 3,
    title: "iDiverse Investment Platform",
    client: "iDiverse",
    category: "branding",
    categoryLabel: "Branding & Web",
    description: "Full website design and logo creation for an investment company. Built with FCA-compliant age-restricted content gates to meet financial regulations.",
    services: ["Logo Design", "Website Design", "FCA Compliance", "Age Verification"],
    results: [
      "Custom brand identity from scratch",
      "FCA regulation compliant",
      "Age-restricted content system"
    ],
    image: "https://customer-assets.emergentagent.com/job_289ce6fc-5925-49ca-8b36-617a014d54b1/artifacts/7bz8c42a_idiverse-desk.png",
    mobileImage: "https://customer-assets.emergentagent.com/job_289ce6fc-5925-49ca-8b36-617a014d54b1/artifacts/wrwtl0ge_idiverse-mob.png",
    url: "https://www.idiverse.co.uk",
    color: "pink"
  },
  {
    id: 4,
    title: "SES Security Services",
    client: "SES Ltd",
    category: "web-design",
    categoryLabel: "Web Design",
    description: "Complete website redesign for a local security firm with national coverage. Built with SEO best practices from the ground up to improve search visibility.",
    services: ["Website Redesign", "SEO Optimisation", "Mobile Responsive"],
    results: [
      "Full website redesign",
      "SEO built from ground up",
      "National service coverage showcase"
    ],
    image: "https://customer-assets.emergentagent.com/job_289ce6fc-5925-49ca-8b36-617a014d54b1/artifacts/fo4scp2b_ses-desk.png",
    mobileImage: "/screenshots/ses-mobile.png",
    url: "https://www.ses.ltd.uk",
    color: "blue"
  },
  {
    id: 5,
    title: "Barton Digital Agency",
    client: "Barton (VZV Ltd)",
    category: "web-design",
    categoryLabel: "Web Design",
    description: "Our own agency website showcasing our full range of digital services. Built with a modern dark theme, integrated AI chatbot, and optimised for conversions.",
    services: ["Website Design", "Live Chat Bot", "SEO", "Contact Forms"],
    results: [
      "Modern, conversion-focused design",
      "AI chatbot integration",
      "Full service showcase"
    ],
    image: "/screenshots/barton.png",
    mobileImage: "/screenshots/barton-mobile.png",
    url: "https://www.barton.uk",
    color: "pink"
  },
];

export default function Work() {
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredProjects = activeCategory === "all" 
    ? projects 
    : projects.filter(project => project.category === activeCategory);

  return (
    <div data-testid="work-page" className="pt-24 md:pt-32 bg-black min-h-screen">
      <Helmet>
        <title>Our Work | Web Design & Digital Marketing Case Studies | Barton</title>
        <meta name="description" content="See our portfolio of successful projects. Real results for real businesses including iSalon, iDiverse, SES Education, and Block P. View our case studies." />
        <meta property="og:title" content="Our Work | Case Studies & Portfolio" />
        <meta property="og:description" content="See real results for real businesses. Web design, e-commerce, and digital marketing case studies from Barton." />
        <meta property="og:url" content="https://barton.uk/work" />
        <meta property="og:image" content="https://barton.uk/og-image.png" />
        <meta name="twitter:title" content="Our Work | Case Studies & Portfolio" />
        <meta name="twitter:description" content="See real results for real businesses. Web design, e-commerce, and digital marketing case studies." />
        <meta name="twitter:image" content="https://barton.uk/og-image.png" />
      </Helmet>
      {/* Hero Section */}
      <section className="container-custom py-12 md:py-16">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500/10 text-purple-400 text-sm font-medium mb-6">
            Our Work
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
            Previous <span className="text-gradient">Projects</span>
          </h1>
          <p className="text-white/60 text-lg leading-relaxed">
            See how we've helped small businesses transform their digital presence 
            and achieve real results.
          </p>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 justify-center mb-12">
          {projectCategories.map((category) => (
            <button
              key={category.slug}
              data-testid={`work-category-${category.slug}`}
              onClick={() => setActiveCategory(category.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeCategory === category.slug
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white"
                  : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="space-y-32">
          {filteredProjects.map((project, index) => (
            <article 
              key={project.id}
              data-testid={`project-${project.id}`}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center py-16 ${
                index % 2 === 1 ? "lg:flex-row-reverse" : ""
              } ${index !== 0 ? "border-t border-white/10 pt-32" : ""}`}
            >
              {/* Image */}
              <div className={`relative rounded-2xl overflow-hidden group ${index % 2 === 1 ? "lg:order-2" : ""}`}>
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-[300px] md:h-[400px] object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                
                {/* Mobile Screenshot Overlay */}
                {project.mobileImage && (
                  <div className="absolute bottom-4 right-4 w-20 md:w-28 rounded-xl overflow-hidden shadow-2xl border-2 border-white/20 transform rotate-3 group-hover:rotate-0 transition-transform duration-500">
                    <img 
                      src={project.mobileImage} 
                      alt={`${project.title} mobile view`}
                      className="w-full h-auto"
                    />
                  </div>
                )}
                
                <div className="absolute bottom-6 left-6">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    project.color === "pink" ? "bg-gradient-to-r from-purple-500 to-pink-500/20 text-pink-400" : "bg-gradient-to-r from-blue-500 to-purple-500/20 text-purple-400"
                  }`}>
                    {project.categoryLabel}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className={`${index % 2 === 1 ? "lg:order-1" : ""}`}>
                <p className="text-white/40 text-sm mb-2">{project.client}</p>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
                  {project.title}
                </h2>
                <p className="text-white/60 text-lg leading-relaxed mb-6">
                  {project.description}
                </p>

                {/* Services */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.services.map((service, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 rounded-full bg-white/5 text-white/60 text-sm"
                    >
                      {service}
                    </span>
                  ))}
                </div>

                {/* Results */}
                <div className="space-y-2 mb-6">
                  <p className="text-white/40 text-sm font-medium uppercase tracking-wider">Results</p>
                  {project.results.map((result, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        project.color === "pink" ? "bg-gradient-to-r from-purple-500 to-pink-500" : "bg-gradient-to-r from-blue-500 to-purple-500"
                      }`} />
                      <span className="text-white text-sm">{result}</span>
                    </div>
                  ))}
                </div>

                {/* Visit Site Button */}
                {project.url && (
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                      project.color === "pink" 
                        ? "bg-gradient-to-r from-purple-500 to-pink-500/10 text-pink-400 hover:bg-gradient-to-r from-purple-500 to-pink-500/20" 
                        : "bg-gradient-to-r from-blue-500 to-purple-500/10 text-purple-400 hover:bg-gradient-to-r from-blue-500 to-purple-500/20"
                    }`}
                  >
                    Visit Site <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-black">
        <div className="container-custom">
          <div className="relative rounded-3xl p-8 md:p-16 text-center text-white overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-transparent to-pink-500/20" />
            <div className="absolute inset-0 border border-white/10 rounded-3xl" />
            
            <div className="relative z-10">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Want results like these?
              </h2>
              <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
                Let's discuss your project and create something amazing together. 
                Every great project starts with a conversation.
              </p>
              <Button
                asChild
                data-testid="work-cta-button"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:bg-gradient-to-r from-purple-500 to-pink-500/90 text-white rounded-full px-10 py-6 text-lg"
              >
                <Link to="/contact">
                  Get In Touch <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
